import { unref, computed } from 'vue';
import { useHead } from '#imports';

function toArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value.filter(Boolean) : [value];
}

function stripHtmlComments(html = '') {
  return String(html)
    .replace(/<!--[\s\S]*?-->/g, '')
    .trim();
}

function parseAttributes(attrString = '') {
  const attrs = {};
  const regex = /([:@\w-]+)(?:=(?:"([^"]*)"|'([^']*)'|([^\s"'>]+)))?/g;

  let match;
  while ((match = regex.exec(attrString))) {
    const [, name, v1, v2, v3] = match;
    const value = v1 ?? v2 ?? v3 ?? true;

    if (name === 'async') attrs.async = true;
    else if (name === 'defer') attrs.defer = true;
    else if (name === 'nomodule') attrs.nomodule = true;
    else if (name === 'crossorigin') attrs.crossorigin = value;
    else if (name === 'referrerpolicy') attrs.referrerpolicy = value;
    else if (name === 'integrity') attrs.integrity = value;
    else if (name === 'type') attrs.type = value;
    else if (name === 'id') attrs.id = value;
    else if (name === 'src') attrs.src = value;
    else if (name === 'height') attrs.height = value;
    else if (name === 'width') attrs.width = value;
    else if (name === 'style') attrs.style = value;
    else if (name.startsWith('data-')) attrs[name] = value;
    else attrs[name] = value;
  }

  return attrs;
}

function extractScripts(rawList, position) {
  const result = [];

  rawList.forEach((raw, rawIndex) => {
    const html = stripHtmlComments(raw);
    if (!html) return;

    const scriptRegex = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
    let match;
    let localIndex = 0;

    while ((match = scriptRegex.exec(html)) !== null) {
      const [, attrString = '', inlineCode = ''] = match;
      const attrs = parseAttributes(attrString);

      result.push({
        key: `cms-script-${position}-${rawIndex}-${attrs.id || attrs.src || localIndex}`,
        tagPosition: position,
        ...attrs,
        ...(attrs.src ? {} : { innerHTML: inlineCode.trim() }),
      });

      localIndex++;
    }

    const hasScriptTags = /<script\b[\s\S]*?<\/script>/i.test(html);
    const hasNoscriptTags = /<noscript\b[\s\S]*?<\/noscript>/i.test(html);

    if (!hasScriptTags && !hasNoscriptTags && !/^<[^>]+>/.test(html)) {
      result.push({
        key: `cms-script-inline-${position}-${rawIndex}`,
        tagPosition: position,
        type: 'text/javascript',
        innerHTML: html,
      });
    }
  });

  return result;
}

function extractNoscripts(rawList, position) {
  const result = [];

  rawList.forEach((raw, rawIndex) => {
    const html = stripHtmlComments(raw);
    if (!html) return;

    const noscriptRegex = /<noscript\b([^>]*)>([\s\S]*?)<\/noscript>/gi;
    let match;
    let localIndex = 0;

    while ((match = noscriptRegex.exec(html)) !== null) {
      const [, attrString = '', inner = ''] = match;
      const attrs = parseAttributes(attrString);

      result.push({
        key: `cms-noscript-${position}-${rawIndex}-${localIndex}`,
        tagPosition: position,
        ...attrs,
        innerHTML: inner.trim(),
      });

      localIndex++;
    }
  });

  return result;
}

export function useCmsScripts(
  scriptsHeadRaw,
  startScriptsBodyRaw,
  endScriptsBodyRaw,
) {
  useHead(
    computed(() => {
      const headRaw = unref(scriptsHeadRaw);
      const bodyOpenRaw = unref(startScriptsBodyRaw);
      const bodyCloseRaw = unref(endScriptsBodyRaw);

      if (!headRaw && !bodyOpenRaw && !bodyCloseRaw) {
        return {};
      }

      const head = toArray(headRaw);
      const bodyOpen = toArray(bodyOpenRaw);
      const bodyClose = toArray(bodyCloseRaw);

      return {
        script: [
          ...extractScripts(head, 'head'),
          ...extractScripts(bodyOpen, 'bodyOpen'),
          ...extractScripts(bodyClose, 'bodyClose'),
        ],
        noscript: [
          ...extractNoscripts(head, 'head'),
          ...extractNoscripts(bodyOpen, 'bodyOpen'),
          ...extractNoscripts(bodyClose, 'bodyClose'),
        ],
      };
    }),
  );
}
